import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './style.css';

const GalleryContext = createContext(null);

export const GalleryImage = ({ src, alt = 'Project screenshot', className, index }) => {
  const gallery = useContext(GalleryContext);

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      onClick={() => gallery && gallery.openPopup(index)}
    />
  );
};

export const FlowShot = ({
  title,
  step,
  layout = 'grid',
  className = 'screenshot-view',
  variant,
  ...imageProps
}) => (
  <div className={`flow-shot${layout === 'single' ? ' flow-shot-single' : ''}`}>
    {(step != null || title) && (
      <div className="wizard-flow-header">
        {step != null ? (
          <span className="wizard-flow-num" aria-hidden="true">{step}</span>
        ) : (
          <span className="flow-shot-bullet" aria-hidden="true"></span>
        )}
        {title && <span className="wizard-flow-title">{title}</span>}
      </div>
    )}
    <GalleryImage className={className} {...imageProps} />
  </div>
);

const ImageGallery = ({ featured = [], imageUrls = [], images = [], children }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const gallery = images.length
    ? images.map((image) => ({
        src: image.src,
        alt: image.alt || image.variant || 'Project screenshot',
        variant: image.variant,
      }))
    : [
        ...featured.map((image) => ({
          src: image.src,
          alt: image.alt || image.variant || 'Project screenshot',
          variant: image.variant,
        })),
        ...imageUrls.map((src, index) => ({
          src,
          alt: `Additional view ${index + 1}`,
        })),
      ];

  const openPopup = (index) => setSelectedImage(index);
  const closePopup = () => setSelectedImage(null);

  const showPrevious = (event) => {
    event.stopPropagation();
    setSelectedImage((current) =>
      current === null ? current : (current - 1 + gallery.length) % gallery.length
    );
  };

  const showNext = (event) => {
    event.stopPropagation();
    setSelectedImage((current) =>
      current === null ? current : (current + 1) % gallery.length
    );
  };

  useEffect(() => {
    if (selectedImage === null) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      } else if (event.key === 'ArrowLeft') {
        setSelectedImage((current) =>
          current === null ? current : (current - 1 + gallery.length) % gallery.length
        );
      } else if (event.key === 'ArrowRight') {
        setSelectedImage((current) =>
          current === null ? current : (current + 1) % gallery.length
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, gallery.length]);

  const thumbStartIndex = featured.length;

  return (
    <GalleryContext.Provider value={{ openPopup }}>
      <div className="project-gallery">
        {children}

        {!children && featured.length > 0 && (
          <div className="row mt-3 featured-views">
            {featured.map((image, index) => (
              <div
                className={image.variant === 'mobile' ? 'col-sm-4' : 'col-sm-8'}
                key={`featured-${index}`}
              >
                <img
                  className={image.variant === 'mobile' ? 'mobile-view' : 'laptop-view'}
                  src={image.src}
                  alt={image.alt || image.variant}
                  onClick={() => openPopup(index)}
                />
              </div>
            ))}
          </div>
        )}

        {!children && imageUrls.length > 0 && (
          <div className="image-gallery row">
            {imageUrls.map((imageUrl, index) => (
              <div className="col-sm-4 mb-3" key={`thumb-${index}`}>
                <img
                  src={imageUrl}
                  alt={`Additional view ${index + 1}`}
                  className="gallery-image"
                  onClick={() => openPopup(thumbStartIndex + index)}
                />
              </div>
            ))}
          </div>
        )}

        {selectedImage !== null && gallery[selectedImage] && createPortal(
          <div className="popup-overlay" onClick={closePopup}>
            {gallery.length > 1 && (
              <button
                type="button"
                className="popup-nav popup-prev"
                onClick={showPrevious}
                aria-label="Previous image"
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>
            )}
            <div className="popup-content" onClick={(event) => event.stopPropagation()}>
              <img
                className={`popup-image popup-${gallery[selectedImage].variant || 'screenshot'}`}
                src={gallery[selectedImage].src}
                alt={gallery[selectedImage].alt}
              />
              {gallery.length > 1 && (
                <p className="popup-caption">
                  {selectedImage + 1} / {gallery.length}
                </p>
              )}
            </div>
            {gallery.length > 1 && (
              <button
                type="button"
                className="popup-nav popup-next"
                onClick={showNext}
                aria-label="Next image"
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            )}
          </div>,
          document.body
        )}
      </div>
    </GalleryContext.Provider>
  );
};

export default ImageGallery;
